import type { LocalGameState } from "../model/LocalGameState";

type Event = Record<string, unknown>;

function str(e: Event, key: string): string | undefined {
  return typeof e[key] === "string" ? (e[key] as string) : undefined;
}

function num(e: Event, key: string): number | undefined {
  return typeof e[key] === "number" ? (e[key] as number) : undefined;
}

function bool(e: Event, key: string): boolean | undefined {
  return typeof e[key] === "boolean" ? (e[key] as boolean) : undefined;
}

function posStr(e: Event, key: string): string {
  const p = e[key] as Record<string, unknown> | undefined;
  if (!p) return "?";
  return `(${p.x}, ${p.y})`;
}

/** Returns a human-readable description of a game event, or null if the event should be silent. */
export function describeEvent(event: Event, state: LocalGameState): string | null {
  const type = str(event, "type");
  if (!type) return null;

  switch (type) {
    case "GameStarted":
      return `Game started! Home: ${state.homeTeamId?.slice(0, 8)} vs Away: ${state.awayTeamId?.slice(0, 8)}`;
    case "CoinTossResolved":
      return `Coin toss: winner chose ${bool(event, "choseOffense") ? "OFFENSE" : "DEFENSE"}`;
    case "HalfStarted":
      return `Half ${num(event, "halfNumber")} started`;
    case "DriveStarted":
      return `Drive started — line of scrimmage y=${num(event, "lineOfScrimmage")}`;
    case "DownStarted":
      return `Down ${num(event, "downNumber")} started (bomb tracker: ${num(event, "bombTracker")})`;
    case "DownEnded":
      return `Down ended: ${str(event, "reason")}`;
    case "DriveEnded":
      return "Drive ended";
    case "HalfEnded":
      return `Half ${num(event, "halfNumber")} ended`;
    case "GameEnded":
      return `GAME OVER — Home ${num(event, "homeScore")} : Away ${num(event, "awayScore")}`;
    case "FormationsRevealed":
      return "Formations revealed!";
    case "PlayerActivated":
      return `${state.getLabel(str(event, "playerId") ?? "")} activated`;
    case "ActivationRoundStarted":
      return `Activation round ${num(event, "roundNumber")} started`;
    case "ActivationRoundEnded":
      return `Activation round ended (clock: ${num(event, "newGameClock")})`;
    case "PlayerMoved": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      return `${label} moved to ${posStr(event, "to")}`;
    }
    case "StandUpAttempted": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      const success = bool(event, "success");
      const remaining = num(event, "movementPointsRemaining");
      if (success) {
        return `${label} stand-up: SUCCESS${remaining !== undefined ? ` (MOV remaining: ${remaining})` : ""}`;
      } else {
        return `${label} stand-up: FAILED`;
      }
    }
    case "TackleAttempted": {
      const a = state.getLabel(str(event, "attackerId") ?? "");
      const d = state.getLabel(str(event, "defenderId") ?? "");
      return `${a} tackles ${d} (roll: ${num(event, "modifiedRoll")})`;
    }
    case "TackleResolved": {
      const a = state.getLabel(str(event, "attackerId") ?? "");
      const d = state.getLabel(str(event, "defenderId") ?? "");
      return `${a} tackles ${d} → ${str(event, "result")}`;
    }
    case "PlayerPushed": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      return `${label} pushed to ${posStr(event, "to")}`;
    }
    case "BallCarrierPushed": {
      const label = state.getLabel(str(event, "carrierId") ?? "");
      const held = bool(event, "held");
      return `${label} hold attempt: roll ${num(event, "modifiedRoll")} — ${held ? "HELD the ball!" : "DROPPED the ball!"}`;
    }
    case "BallCarrierWentProne": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      const held = bool(event, "held");
      return `${label} went prone with the ball: hold roll ${num(event, "modifiedRoll")} — ${held ? "HELD the ball!" : "DROPPED the ball!"}`;
    }
    case "BallDroppedFromProne": {
      const label = state.getLabel(str(event, "carrierId") ?? "");
      return `${label} dropped the ball at ${posStr(event, "dropPosition")}, scattering ${str(event, "scatterDirection")}`;
    }
    case "BallCarrierLockedUp": {
      const label = state.getLabel(str(event, "carrierId") ?? "");
      const held = bool(event, "held");
      return `${label} hold attempt: roll ${num(event, "modifiedRoll")} — ${held ? "HELD the ball!" : "DROPPED the ball!"}`;
    }
    case "BallDroppedFromLockUp": {
      const label = state.getLabel(str(event, "carrierId") ?? "");
      return `${label} dropped the ball at ${posStr(event, "dropPosition")}, scattering ${str(event, "scatterDirection")}`;
    }
    case "LockUpInitiated": {
      const a = state.getLabel(str(event, "attackerId") ?? "");
      const d = state.getLabel(str(event, "defenderId") ?? "");
      return `${a} and ${d} are locked up!`;
    }
    case "LockUpBroken": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      const causeLabel = state.getLabel(str(event, "causePlayerId") ?? "");
      return `${causeLabel} broke ${label} free from lock-up`;
    }
    case "InjuryApplied": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      return `${label} injured: ${str(event, "effect")}`;
    }
    case "StunCleared":
      return `${state.getLabel(str(event, "playerId") ?? "")} stun cleared (still prone)`;
    case "PassAttempted": {
      const label = state.getLabel(str(event, "throwerId") ?? "");
      return `${label} throws to ${posStr(event, "targetPosition")} (roll: ${num(event, "modifiedRoll")})`;
    }
    case "PushAttempted": {
      const a = state.getLabel(str(event, "attackerId") ?? "");
      const d = state.getLabel(str(event, "defenderId") ?? "");
      return `${a} attempts to push ${d} (roll: ${num(event, "modifiedRoll")})`;
    }
    case "PushRollResolved": {
      const a = state.getLabel(str(event, "attackerId") ?? "");
      const d = state.getLabel(str(event, "defenderId") ?? "");
      const result = str(event, "result");
      return `${a} vs ${d} push: ${result === "PUSH_SUCCESS" ? "PUSHED!" : "LOCKED UP"}`;
    }
    case "CatchAttempted": {
      const label = state.getLabel(str(event, "playerId") ?? "");
      return `${label} catch attempt: ${bool(event, "caught") ? "CAUGHT!" : "dropped"}`;
    }
    case "BallLanded":
      return `Ball landed at ${posStr(event, "position")}`;
    case "PossessionChanged": {
      const toTeam = str(event, "toTeamId");
      const side = toTeam === state.homeTeamId ? "HOME" : "AWAY";
      return `Possession changed to ${side} (${str(event, "reason")})`;
    }
    case "TouchdownScored": {
      const team = str(event, "scoringTeamId");
      const label = state.getLabel(str(event, "scoringPlayerId") ?? "");
      const side = team === state.homeTeamId ? "HOME" : "AWAY";
      return `${side} TOUCHDOWN by ${label} (+${num(event, "points")})!`;
    }
    case "BombTrackerIncremented":
      return `Bomb tracker: ${num(event, "newValue")}`;
    case "BombExplosionTriggered":
      return "BOOM! Bomb explodes!";
    case "BombExplosionResolved": {
      const label = state.getLabel(str(event, "carrierId") ?? "");
      return `${label} caught in explosion: ${str(event, "result")}`;
    }
    case "FirstDownAchieved": {
      const bp = event["newBallPosition"] as Record<string, unknown> | undefined;
      return `First down! New LoS: y=${bp?.y}`;
    }
    case "DownCounterIncremented":
      return `Down ${num(event, "newDownNumber")}`;
    case "PlayerKnelt":
      return `${state.getLabel(str(event, "playerId") ?? "")} takes a knee — down ends`;
    default:
      return null;
  }
}
